import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminOrManagerRoute from './components/Auth/AdminOrManagerRoute';
import Login from './components/Auth/Login';
import CallForm from './components/CallForm/CallForm';
import LeadForm from './components/LeadForm';
import LeadFormSnappedWave from './components/LeadForm-SnappedWave';
import LeadFormSnapchat from './components/LeadForm-Snapchat';
import LeadFormCollab from './components/LeadForm-Collab';
import LeadFormTCS from './components/LeadForm-TCS';
import DynamicLeadForm from './components/Forms/DynamicLeadForm';
import UploadTracker from './components/UploadTracker/UploadTracker';
import LeadTracker from './components/Lead/LeadTracker';
import LeadTracker2 from './components/Lead/LeadTracker2';
import SignContract from './components/SignContract/SignContract';
import OnboardingForm from './components/OnboardingForm/OnboardingForm';
import TaskGrid from './components/TaskManager/TaskGrid';
import FileManager from './components/CDN/FileManager';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import TimeSheet from './components/TimeSheet/TimeSheet';
import CDNLogin from './components/Auth/CDNLogin';
import TimeSheetLogin from './components/Auth/TimeSheetLogin';
import DemoSite from './components/DemoSite/DemoSite';
import Privacy from './components/Privacy/Privacy';
import OAuthCallback from './components/Email/OAuthCallback';
import Support from './components/Support/Support';
import VideoSummary from './components/AIReview/VideoSummary';
import Survey from './components/survey/survey';
import AdminSurvey from './components/survey/AdminDashboard';
import AIChat from './components/AIChat/AIChat';
import DesktopUpload from './components/DesktopUpload/DesktopUpload';
import SurveyLogin from './components/Auth/SurveyLogin';
import SplitAssignment from './components/Payments/SplitAssignment';
import PayeeForm from './components/Payments/PayeeForm';
import CallFormMoxy from './components/CallForm/CallForm-moxy';
import { FileMover } from './components/FileMover/file-mover';
import EmployeeDashboard from './components/Employee/EmployeeDashboard';
import DocumentReview from './components/DocumentReview/DocumentReview';
import FormsManager from './components/Forms/FormsManager';
import PaymentDashboard from './components/Payments/PaymentDashboard';
import ScreenshotUpload from './components/Screenshots/ScreenshotUpload';
import PixelMonetizationForm from './components/PixelMonetization/PixelMonetizationForm';
import TOSTemplateManager from './components/PixelMonetization/TOSTemplateManager';
import SnapchatIntakeForm from './components/SnapchatIntakeForm';
import SnapchatIntakeBulk from './components/SnapchatIntakeBulk';
import AutomationManager from './components/Automations/AutomationManager';
import StaffNotificationManager from './components/Notifications/StaffNotificationManager';
import Home from './components/Home/Home';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import { getDynamicPermissions } from './utils/dynamicPermissions';
import './global.css';

/**
 * Main application component that sets up routing and authentication
 * Wraps the entire application in AuthProvider and Router components
 * Defines protected and public routes
 * 
 * @component
 * @returns {React.ReactElement} The rendered App component
 */
function App() {
  // Load dynamic permissions on app start (only if user is logged in)
  useEffect(() => {
    async function loadPermissions() {
      try {
        // Wait a bit for auth to initialize
        setTimeout(async () => {
          try {
            const perms = await getDynamicPermissions();
            if (perms) {
              window.__DYNAMIC_PERMISSIONS__ = perms;
              console.log('[APP] Loaded dynamic permissions:', perms);
            } else {
              console.warn('[APP] No dynamic permissions returned');
            }
          } catch (e) {
            console.warn('[APP] Could not load dynamic permissions:', e);
          }
        }, 1000);
      } catch (e) {
        console.error('[APP] Error in loadPermissions:', e);
      }
    }
    loadPermissions();
  }, []);

  return (
    <AuthProvider>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 2500,
          style: {
            background: '#fff',
            color: '#363636',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Router>
        <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/cdn-login" element={<CDNLogin />} />
          <Route path="/timesheet-login" element={<TimeSheetLogin />} />
          <Route path="/survey-login" element={<SurveyLogin />} />
          
          {/* Public Routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/call" element={<CallForm />} />
          <Route path="/call-moxy" element={<CallFormMoxy />} />
          <Route path="/lead-form" element={<LeadForm />} />
          <Route path="/snapwave" element={<LeadFormSnappedWave />} />
          <Route path="/snapchat" element={<LeadFormSnapchat />} />
          <Route path="/snapchat-intake" element={<SnapchatIntakeForm />} />
          <Route path="/intake-form/:partnerSlug" element={<SnapchatIntakeForm />} />
          <Route path="/snapchat-intake-bulk" element={<SnapchatIntakeBulk />} />
          <Route path="/collab" element={<LeadFormCollab />} />
          <Route path="/tcs" element={<LeadFormTCS />} />
          <Route path="/apply/:formSlug" element={<DynamicLeadForm />} />
          <Route path="/sign-contract/:clientId" element={<SignContract />} />
          <Route path="/review/:revisionId" element={<DocumentReview />} />
          <Route path="/demo" element={<DemoSite />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/emailalias" element={<OAuthCallback />} />
          <Route path="/support" element={<Support />} />
          <Route path="/payee-form" element={<PayeeForm />} />
          <Route path="/screenshots" element={<ScreenshotUpload />} />
          
          {/* Survey Route - handles its own auth */}
          <Route path="/survey" element={<Survey />} />
          
          {/* Snap Monetization Route - redirect to pixel-monetization */}
          <Route path="/snap-monetization" element={<Navigate to="/pixel-monetization" replace />} />
          
          {/* Pixel Monetization Route - handles its own auth */}
          <Route path="/pixel-monetization" element={<PixelMonetizationForm />} />
          
          {/* Protected Routes */}
          
          <Route path="/ai-review/video-summary" element={
            <ProtectedRoute>
              <VideoSummary />
            </ProtectedRoute>
          } />

          <Route path="/cdn-manager" element={
            <ProtectedRoute>
              <FileManager />
            </ProtectedRoute>
          } />
          
          <Route path="/upload-activity" element={
            <ProtectedRoute>
              <UploadTracker />
            </ProtectedRoute>
          } />
          
          <Route path="/lead" element={
            <ProtectedRoute>
              <LeadTracker />
            </ProtectedRoute>
          } />
          
          <Route path="/lead2" element={
            <ProtectedRoute>
              <LeadTracker2 />
            </ProtectedRoute>
          } />
          
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <OnboardingForm />
            </ProtectedRoute>
          } />
          
          <Route path="/tasks" element={
            <ProtectedRoute>
              <TaskGrid />
            </ProtectedRoute>
          } />

          <Route path="/forms" element={
            <AdminOrManagerRoute>
              <FormsManager />
            </AdminOrManagerRoute>
          } />
          
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/analytics/dashboard" element={
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/data" element={
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/timesheet" element={
            <ProtectedRoute>
              <TimeSheet />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/survey" element={
            <ProtectedRoute>
              <AdminSurvey />
            </ProtectedRoute>
          } />
          
          <Route path="/chat" element={
            <ProtectedRoute>
              <AIChat />
            </ProtectedRoute>
          } />
          
          <Route path="/desktop-upload" element={
            <ProtectedRoute>
              <DesktopUpload />
            </ProtectedRoute>
          } />
          
          <Route path="/filemover" element={
            <ProtectedRoute>
              <FileMover />
            </ProtectedRoute>
          } />
          
          <Route path="/payments/splits" element={
            <ProtectedRoute>
              <SplitAssignment />
            </ProtectedRoute>
          } />
          
          <Route path="/payments/dashboard" element={
            <ProtectedRoute redirectTo="/login">
              <PaymentDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/employees" element={
            <AdminOrManagerRoute>
              <EmployeeDashboard />
            </AdminOrManagerRoute>
          } />
          
          <Route path="/automations" element={
            <AdminOrManagerRoute>
              <AutomationManager />
            </AdminOrManagerRoute>
          } />
          
          <Route path="/staff-notifications" element={
            <AdminOrManagerRoute>
              <StaffNotificationManager />
            </AdminOrManagerRoute>
          } />
          
          <Route path="/tos-templates" element={
            <AdminOrManagerRoute>
              <TOSTemplateManager />
            </AdminOrManagerRoute>
          } />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Navigate to="/lead" replace />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/call" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;